import React from "react";
import { Link } from "react-router-dom";

/**
 * The shell every list on a calf sheet shares: a bare stack on mobile, a bordered
 * card with a header and an "Ajouter" button from `md` up.
 *
 * Mobile gets the add action as a plain link above the list, because on a phone a
 * new record opens its own page rather than a dialog.
 */
export function ListCard({ title, addLabel, addTo, onAdd, children }) {
  return (
    <>
      {addTo && (
        <div className="mt-3 flex flex-row justify-end md:hidden">
          <Link to={addTo}>
            <span className="font-medium text-amber-500 dark:text-amber-400">
              {addLabel}
            </span>
          </Link>
        </div>
      )}

      <div className="mt-3 w-full overflow-visible rounded-lg border-stone-200 bg-white md:mt-6 md:border md:shadow dark:border-slate-700 dark:bg-slate-900">
        <div className="mb-3 hidden justify-between rounded-t-md border-b border-stone-200 p-6 text-stone-800 md:flex dark:border-slate-700 dark:text-white">
          <span className="text-lg font-semibold">{title}</span>
          <button
            onClick={onAdd}
            className="flex items-center rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-800 shadow transition-all duration-100 hover:-translate-y-[2px] hover:shadow-lg dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <PlusIcon />
            <span>Ajouter</span>
          </button>
        </div>
        {children}
      </div>
    </>
  );
}

/** Shown in place of the list when a calf has no record of this kind yet. */
export function EmptyState({ title, description, actionLabel, actionTo }) {
  return (
    <div className="mt-3 flex flex-col items-center justify-center py-6">
      <span className="font-semibold dark:text-white">{title}</span>
      <p className="my-3 text-center text-sm text-stone-400 dark:text-slate-400">
        {description}
      </p>
      {actionTo && (
        <Link to={actionTo} className="md:hidden">
          <button className="rounded-lg bg-amber-400 py-1 px-3 text-white dark:bg-amber-500 dark:hover:bg-amber-600">
            {actionLabel}
          </button>
        </Link>
      )}
    </div>
  );
}

export function PlusIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="mr-2 h-4 w-4"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}

export function ChevronIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="h-5 w-5 text-stone-400"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  );
}

/**
 * One tappable row. It stays a real link so the mobile route works with the
 * keyboard and with open-in-new-tab; on desktop the click is intercepted and
 * opens the dialog instead.
 */
export function ListRow({ to, onDesktopClick, isLast, children }) {
  return (
    <Link
      to={to}
      onClick={(event) => {
        if (onDesktopClick && onDesktopClick(event) === false) {
          event.preventDefault();
        }
      }}
      className="block"
    >
      <div
        className={
          "ml-3 flex items-center py-2 transition-all duration-100 md:cursor-pointer md:hover:translate-x-1" +
          (isLast ? "" : " border-b dark:border-slate-700")
        }
      >
        {children}
      </div>
    </Link>
  );
}
